import { useMutation } from "@tanstack/react-query";

import { PricingTierQuote } from "@models/Quote";
import { PricingTier } from "@models/RequestForQuote";
import client, { useMutateError } from "@services/api";
import asyncPool from "@utility/asyncPool";

import { QuoteFormType } from "../SupplierQuote/QuoteForm";

type DirtyPricingTierQuote = QuoteFormType["pricingTierQuotes"][number] & {
  dirtyPT: boolean;
  dirtyPTQ: boolean;
};
type SavePricingTierQuotesMutationPayload = {
  quoteId: string;
  rfqId: string;
  pricingTierQuotes: DirtyPricingTierQuote[];
};

async function savePricingTierQuote(
  quoteId: string,
  rfqId: string,
  {
    id,
    isMarkedForDeletion,
    pricingTierId,
    isCustomPricingTier,
    dirtyPT,
    dirtyPTQ,
    ...ptq
  }: DirtyPricingTierQuote
) {
  if (isMarkedForDeletion) {
    await client.delete(`pricing-tiers/${pricingTierId}`);
    return;
  }
  if (isCustomPricingTier) {
    if (!pricingTierId) {
      const res = await client
        .post<PricingTier>("pricing-tiers", {
          __type: "pricing-tier",
          qty: ptq.pricingTierQty,
          requestForQuoteId: rfqId,
        })
        .then((res) => res.data);
      pricingTierId = res.id;
    } else if (dirtyPT) {
      await client.update<PricingTier>(`pricing-tiers/${pricingTierId}`, {
        __type: "pricing-tier",
        qty: ptq.pricingTierQty,
      });
    }
  }

  if (!id) {
    await client.post<PricingTierQuote>("pricing-tier-quotes", {
      __type: "pricing-tier-quote",
      ...ptq,
      quoteId: +quoteId,
      pricingTierId: +pricingTierId,
    });
  } else if (dirtyPTQ) {
    await client.update<PricingTierQuote>(`pricing-tier-quotes/${id}`, {
      __type: "pricing-tier-quote",
      ...ptq,
    });
  }
}

function useSavePricingTierQuotes() {
  const setMutateError = useMutateError();
  return useMutation({
    mutationFn: async ({
      quoteId,
      rfqId,
      pricingTierQuotes,
    }: SavePricingTierQuotesMutationPayload) => {
      const res = await asyncPool(5, pricingTierQuotes, (ptq) =>
        savePricingTierQuote(quoteId, rfqId, ptq)
      );
      if (res.errors) throw res.errors[0];
      return res.results;
    },
    onError: (error) => {
      setMutateError(error, "Save pricing tier quotes");
    },
  });
}

export default useSavePricingTierQuotes;
